import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import MapComponent from '../components/GoogleMapComponent';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { mockDriver, mockRides, mockLocations } from '../mock';
import { 
  Car, DollarSign, Star, MapPin, Clock, 
  User, LogOut, Home, TrendingUp, Navigation,
  Phone, MessageSquare, CheckCircle, XCircle
} from 'lucide-react';

const DriverDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language === 'ar';
  
  const [currentView, setCurrentView] = useState('home'); // home, rideRequest, activeRide, earnings, profile
  const [isOnline, setIsOnline] = useState(mockDriver.isOnline);
  const [showRideRequest, setShowRideRequest] = useState(false);
  const [currentRide, setCurrentRide] = useState(null);
  const [rideStatus, setRideStatus] = useState('idle'); // idle, accepted, pickedUp, completed
  
  const handleToggleOnline = () => {
    setIsOnline(!isOnline);
    if (!isOnline) {
      // Simulate ride request after going online
      setTimeout(() => {
        setShowRideRequest(true);
      }, 3000);
    }
  };
  
  const handleAcceptRide = () => {
    setShowRideRequest(false);
    setCurrentRide({
      passengerId: 'p1',
      passengerName: 'John Doe',
      passengerPhone: '+963-123-456789',
      passengerRating: 4.8,
      pickup: mockLocations[0],
      dropoff: mockLocations[1],
      fare: 7500,
      distance: '5.2 km',
      duration: '15 min'
    });
    setRideStatus('accepted');
    setCurrentView('activeRide');
  };
  
  const handleDeclineRide = () => {
    setShowRideRequest(false);
  };
  
  const handleArrived = () => {
    setRideStatus('arrived');
  };
  
  const handlePickup = () => {
    setRideStatus('pickedUp');
  };
  
  const handleComplete = () => {
    setRideStatus('completed');
    setTimeout(() => {
      setCurrentView('home');
      setRideStatus('idle');
      setCurrentRide(null);
    }, 2000);
  };
  
  const renderHome = () => (
    <div className="space-y-6">
      {/* Online Status Card */}
      <Card className="p-6 bg-gradient-to-r from-black to-black text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold mb-1">
              {isOnline ? t('online') : t('offline')}
            </h2>
            <p className="text-black">
              {isOnline 
                ? (isArabic ? 'أنت متاح لقبول الرحلات' : 'You are available for rides')
                : (isArabic ? 'اضغط للاتصال وقبول الرحلات' : 'Go online to accept rides')
              }
            </p>
          </div>
          <Switch 
            checked={isOnline} 
            onCheckedChange={handleToggleOnline}
            className="data-[state=checked]:bg-white"
          />
        </div>
        
        {isOnline && (
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center gap-2 text-white">
              <MapPin className="h-5 w-5" />
              <span className="font-medium">{isArabic ? 'جاهز لقبول الرحلات...' : 'Ready to accept rides...'}</span>
            </div>
          </div>
        )}
      </Card>
      
      {/* Today's Earnings */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-green-600" />
          {t('todayEarnings')}
        </h3>
        <div className="text-4xl font-bold text-gray-900 mb-2">
          {mockDriver.earnings.today.toLocaleString()}
          <span className="text-lg text-gray-600 ml-2">{isArabic ? 'ل.س' : 'SYP'}</span>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Car className="h-4 w-4" />
            <span>{mockRides.length} {t('rides')}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>8.5 {isArabic ? 'ساعة' : 'hours'}</span>
          </div>
        </div>
      </Card>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-black" />
            <span className="text-sm text-gray-600">{t('weekEarnings')}</span>
          </div>
          <p className="text-2xl font-bold">{mockDriver.earnings.week.toLocaleString()}</p>
          <p className="text-xs text-gray-500">{isArabic ? 'ل.س' : 'SYP'}</p>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Star className="h-5 w-5 text-yellow-500" />
            <span className="text-sm text-gray-600">{t('rating')}</span>
          </div>
          <p className="text-2xl font-bold">{mockDriver.rating}</p>
          <p className="text-xs text-gray-500">{mockDriver.totalRides} {t('rides')}</p>
        </Card>
      </div>
      
      {/* Map */}
      <div className="rounded-xl overflow-hidden shadow-lg">
        <MapComponent 
          center={mockDriver.location}
          driverLocation={mockDriver.location}
          height="300px"
        />
      </div>
    </div>
  );
  
  const renderActiveRide = () => (
    <div className="space-y-6">
      <div className="rounded-xl overflow-hidden shadow-lg">
        <MapComponent 
          center={currentRide.pickup}
          pickup={currentRide.pickup}
          dropoff={currentRide.dropoff}
          driverLocation={mockDriver.location}
          showRoute={true}
          height="400px"
        />
      </div>
      
      {/* Passenger Info */}
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-16 w-16">
              <AvatarImage src={`https://ui-avatars.com/api/?name=${encodeURIComponent(currentRide.passengerName || 'Passenger')}&background=000000&color=fff`} />
              <AvatarFallback>{(currentRide.passengerName || 'P').charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-lg">{currentRide.passengerName}</h3>
              <p className="text-sm text-gray-600">{currentRide.passengerPhone}</p>
              <div className="flex items-center gap-1 mt-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-medium">{currentRide.passengerRating}</span>
              </div>
            </div>
          </div>
          <Badge className={rideStatus === 'accepted' ? 'bg-blue-100 text-blue-800' : rideStatus === 'pickedUp' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
            {rideStatus === 'accepted' && (isArabic ? 'في الطريق' : 'On the way')}
            {rideStatus === 'arrived' && (isArabic ? 'وصلت' : 'Arrived')}
            {rideStatus === 'pickedUp' && (isArabic ? 'جاري الرحلة' : 'In Progress')}
            {rideStatus === 'completed' && (isArabic ? 'اكتملت' : 'Completed')}
          </Badge>
        </div>
        
        {/* Route Info */}
        <div className="space-y-3 mb-4">
          <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
            <MapPin className="h-5 w-5 text-green-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-1">{t('pickup')}</p>
              <p className="font-medium">{isArabic ? currentRide.pickup.nameAr : currentRide.pickup.name}</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
            <MapPin className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-1">{t('dropoff')}</p>
              <p className="font-medium">{isArabic ? currentRide.dropoff.nameAr : currentRide.dropoff.name}</p>
            </div>
          </div>
        </div>
        
        {/* Ride Details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">{isArabic ? 'المسافة' : 'Distance'}</p>
              <p className="font-semibold">{currentRide.distance}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">{isArabic ? 'الوقت' : 'Duration'}</p>
              <p className="font-semibold">{currentRide.duration}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-green-500">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">{isArabic ? 'الأجرة' : 'Fare'}</span>
              <span className="text-2xl font-bold text-black">{currentRide.fare.toLocaleString()} {isArabic ? 'ل.س' : 'SYP'}</span>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2 mb-3">
          <Button className="flex-1" variant="outline">
            <Phone className="mr-2 h-4 w-4" />
            {isArabic ? 'اتصال' : 'Call'}
          </Button>
          <Button className="flex-1" variant="outline">
            <MessageSquare className="mr-2 h-4 w-4" />
            {isArabic ? 'رسالة' : 'Message'}
          </Button>
        </div>
        
        {rideStatus === 'accepted' && (
          <Button className="w-full bg-gradient-to-r from-black to-black" onClick={handleArrived}>
            <Navigation className="mr-2 h-4 w-4" />
            {isArabic ? 'وصلت إلى موقع الالتقاط' : 'Arrived at Pickup'}
          </Button>
        )}
        
        {rideStatus === 'arrived' && (
          <Button className="w-full bg-gradient-to-r from-green-600 to-emerald-600" onClick={handlePickup}>
            <CheckCircle className="mr-2 h-4 w-4" />
            {t('startRide')}
          </Button>
        )}
        
        {rideStatus === 'pickedUp' && (
          <Button className="w-full bg-gradient-to-r from-green-600 to-emerald-600" onClick={handleComplete}>
            <CheckCircle className="mr-2 h-4 w-4" />
            {t('completeRide')}
          </Button>
        )}
        
        {rideStatus === 'completed' && (
          <div className="text-center py-4">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-3" />
            <p className="text-xl font-semibold text-gray-900">{t('completed')}!</p>
            <p className="text-gray-600">{isArabic ? 'تم إضافة الأرباح إلى حسابك' : 'Earnings added to your account'}</p>
          </div>
        )}
      </Card>
    </div>
  );
  
  const renderEarnings = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">{t('earnings')}</h2>
      
      {/* Total Earnings */}
      <Card className="p-6 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <p className="text-sm mb-2">{t('monthEarnings')}</p>
        <p className="text-4xl font-bold mb-1">{mockDriver.earnings.month.toLocaleString()}</p>
        <p className="text-sm text-green-100">{isArabic ? 'ليرة سورية' : 'Syrian Pounds'}</p>
      </Card>
      
      {/* Breakdown */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <p className="text-sm text-gray-600 mb-2">{t('todayEarnings')}</p>
          <p className="text-2xl font-bold">{mockDriver.earnings.today.toLocaleString()}</p>
          <p className="text-xs text-gray-500">{isArabic ? 'ل.س' : 'SYP'}</p>
        </Card>
        
        <Card className="p-4">
          <p className="text-sm text-gray-600 mb-2">{t('weekEarnings')}</p>
          <p className="text-2xl font-bold">{mockDriver.earnings.week.toLocaleString()}</p>
          <p className="text-xs text-gray-500">{isArabic ? 'ل.س' : 'SYP'}</p>
        </Card>
      </div>
      
      {/* Recent Rides */}
      <div>
        <h3 className="font-semibold mb-4">{isArabic ? 'الرحلات الأخيرة' : 'Recent Rides'}</h3>
        {mockRides.map((ride) => (
          <Card key={ride.id} className="p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">{ride.passengerName}</span>
              <span className="font-bold text-green-600">+{ride.fare.toLocaleString()} {isArabic ? 'ل.س' : 'SYP'}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>{ride.distance}</span>
              <span>•</span>
              <span>{ride.duration}</span>
              <span>•</span>
              <span>{new Date(ride.date).toLocaleDateString(isArabic ? 'ar-SY' : 'en-US')}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
  
  const renderProfile = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="h-20 w-20">
            <AvatarImage src={mockDriver.profileImage} />
            <AvatarFallback>{(mockDriver?.name || 'D').charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <h2 className="text-2xl font-bold">{mockDriver.name}</h2>
            <p className="text-gray-600">{mockDriver.email}</p>
            <p className="text-gray-600">{mockDriver.phone}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <Star className="h-6 w-6 mx-auto mb-2 text-black" />
            <p className="text-2xl font-bold text-black">{mockDriver.rating}</p>
            <p className="text-sm text-gray-600">{t('rating')}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <Car className="h-6 w-6 mx-auto mb-2 text-black" />
            <p className="text-2xl font-bold text-black">{mockDriver.totalRides}</p>
            <p className="text-sm text-gray-600">{t('totalRides')}</p>
          </div>
        </div>
      </Card>
      
      <Card className="p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Car className="h-5 w-5" />
          {isArabic ? 'معلومات السيارة' : 'Vehicle Info'}
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">{isArabic ? 'الطراز' : 'Model'}</span>
            <span className="font-medium">{mockDriver.carModel}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">{isArabic ? 'اللوحة' : 'Plate'}</span>
            <span className="font-medium">{mockDriver.carNumber}</span>
          </div>
        </div>
      </Card>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/saeed-logo.png" alt="Saeed" className="h-10 w-10 object-contain bg-black rounded-lg p-1" />
            <h1 className="text-xl font-bold tracking-wide">SAEED {isArabic ? 'للسائقين' : 'Driver'}</h1>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {currentView === 'home' && renderHome()}
        {currentView === 'activeRide' && renderActiveRide()}
        {currentView === 'earnings' && renderEarnings()}
        {currentView === 'profile' && renderProfile()}
      </main>
      
      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t">
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-around">
          <Button 
            variant="ghost" 
            className={currentView === 'home' ? 'text-black' : 'text-gray-600'}
            onClick={() => setCurrentView('home')}
          >
            <div className="flex flex-col items-center gap-1">
              <Home className="h-5 w-5" />
              <span className="text-xs">{isArabic ? 'الرئيسية' : 'Home'}</span>
            </div>
          </Button>
          <Button 
            variant="ghost" 
            className={currentView === 'earnings' ? 'text-black' : 'text-gray-600'}
            onClick={() => setCurrentView('earnings')}
          >
            <div className="flex flex-col items-center gap-1">
              <DollarSign className="h-5 w-5" />
              <span className="text-xs">{isArabic ? 'الأرباح' : 'Earnings'}</span>
            </div>
          </Button>
          <Button 
            variant="ghost" 
            className={currentView === 'profile' ? 'text-black' : 'text-gray-600'}
            onClick={() => setCurrentView('profile')}
          >
            <div className="flex flex-col items-center gap-1">
              <User className="h-5 w-5" />
              <span className="text-xs">{isArabic ? 'الملف' : 'Profile'}</span>
            </div>
          </Button>
        </div>
      </nav>
      
      {/* Ride Request Dialog */}
      <Dialog open={showRideRequest} onOpenChange={setShowRideRequest}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('newRideRequest')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-black mb-2">7,500 {isArabic ? 'ل.س' : 'SYP'}</p>
              <p className="text-sm text-gray-600">5.2 km • 15 min</p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                <MapPin className="h-5 w-5 text-green-600" />
                <span className="text-sm">{mockLocations[0].name}</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
                <MapPin className="h-5 w-5 text-red-600" />
                <span className="text-sm">{mockLocations[1].name}</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                className="flex-1" 
                variant="outline"
                onClick={handleDeclineRide}
              >
                <XCircle className="mr-2 h-4 w-4" />
                {t('declineRide')}
              </Button>
              <Button 
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600"
                onClick={handleAcceptRide}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                {t('acceptRide')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverDashboard;